import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const STEPS = [
  {
    number: '01',
    title: 'Upload Your .docx File',
    desc: 'Drag and drop or click to upload any Word document. Supports .docx files up to 20 MB.',
  },
  {
    number: '02',
    title: 'Select a Template',
    desc: 'Choose from IEEE Conference, Springer LNCS,  or plain LaTeX. We format everything to spec.',
  },
  {
    number: '03',
    title: 'Download Ready-to-Compile LaTeX',
    desc: 'Get a clean .tex file you can open in LaTeXLaB or any LaTeX editor — no cleanup needed.',
  },
];

const TEMPLATES = [
  {
    name: 'IEEE Conference',
    tag: 'IEEE',
    color: '#1d4ed8',
    bg: '#eff6ff',
    desc: 'Two-column format for IEEE conference and journal submissions.',
  },
  {
    name: 'Springer LNCS',
    tag: 'LNCS',
    color: '#7c3aed',
    bg: '#f5f3ff',
    desc: 'Lecture Notes in Computer Science format for Springer publications.',
  },
  {
    name: 'Plain LaTeX',
    tag: 'Plain',
    color: '#16a34a',
    bg: '#f0fdf4',
    desc: 'Standard article class — a clean base you can customise freely.',
  },
  {
  },
];

const FEATURES = [
  {
    icon: '📝',
    title: 'Title & Abstract Extraction',
    desc: 'Automatically detects and maps your document title and abstract into the correct LaTeX macros.',
  },
  {
    icon: '🖼️',
    title: 'Figure Handling',
    desc: 'Inline figures are extracted and referenced with \\includegraphics and proper \\caption placement.',
  },
  {
    icon: '📚',
    title: 'BibTeX References',
    desc: 'Your bibliography is converted to a .bib file with proper cite keys — ready for \\cite{}.',
  },
  {
    icon: '⚡',
    title: 'One-Click Compilation',
    desc: 'Open the result directly in LaTeXLaB and compile to PDF in the browser — no local install needed.',
  },
];

export default function WordToLatexPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Free Word to LaTeX Converter Online | LaTeXLaB';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content =
      'Convert Word (.docx) to LaTeX instantly. Supports IEEE Conference, Springer LNCS. Free online tool for researchers and students.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://latexlab.in/word-to-latex';

    return () => {
      document.title = 'LaTeXLaB';
    };
  }, []);

  return (
    <div style={s.page}>
      {/* ── Hero ── */}
      <section style={s.hero}>
        <div style={s.heroInner}>
          <span style={s.heroBadge}>Free Online Tool</span>
          <h1 style={s.h1}>Convert Word Document to LaTeX Online — Free</h1>
          <p style={s.heroSub}>
            Upload your <strong>.docx</strong> file and get ready-to-compile LaTeX in seconds.
            No manual formatting needed.
          </p>
          <button style={s.ctaBtn} onClick={() => navigate('/convert')}>
            Try Free Converter →
          </button>
          <p style={s.heroNote}>No sign-up required &nbsp;·&nbsp; Works in your browser</p>
        </div>
      </section>

      {/* ── How it works ── */}
      <section style={s.section}>
        <div style={s.container}>
          <h2 style={s.h2}>How It Works</h2>
          <p style={s.sectionSub}>Three steps from Word to publication-ready LaTeX.</p>
          <div style={s.stepsRow}>
            {STEPS.map((step, i) => (
              <div key={i} style={s.stepCard}>
                <span style={s.stepNumber}>{step.number}</span>
                <h3 style={s.stepTitle}>{step.title}</h3>
                <p style={s.stepDesc}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Supported Templates ── */}
      <section style={{ ...s.section, background: '#f0fdf4' }}>
        <div style={s.container}>
          <h2 style={s.h2}>Supported Templates</h2>
          <p style={s.sectionSub}>
            We apply the exact class files and formatting rules each venue requires.
          </p>
          <div style={s.templatesRow}>
            {TEMPLATES.map((t, i) => (
              <div key={i} style={s.templateCard}>
                <span style={{ ...s.templateTag, color: t.color, background: t.bg }}>
                  {t.tag}
                </span>
                <h3 style={s.templateName}>{t.name}</h3>
                <p style={s.templateDesc}>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section style={s.section}>
        <div style={s.container}>
          <h2 style={s.h2}>What Gets Converted</h2>
          <p style={s.sectionSub}>
            Every element of your Word document is handled — not just the text.
          </p>
          <div style={s.featuresGrid}>
            {FEATURES.map((f, i) => (
              <div key={i} style={s.featureCard}>
                <span style={s.featureIcon}>{f.icon}</span>
                <h3 style={s.featureTitle}>{f.title}</h3>
                <p style={s.featureDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section style={s.ctaSection}>
        <div style={s.ctaBox}>
          <h2 style={s.ctaHeading}>Ready to convert your document?</h2>
          <p style={s.ctaSub}>
            Free for all users. No account needed to try it out.
          </p>
          <button style={s.ctaBtn} onClick={() => navigate('/convert')}>
            Try Free Converter →
          </button>
        </div>
      </section>
    </div>
  );
}

const GREEN = '#16a34a';
const GREEN_LIGHT = '#22c55e';

const s = {
  page: {
    minHeight: '100vh',
    background: '#ffffff',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    color: '#0f172a',
  },

  /* Hero */
  hero: {
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 60%, #ffffff 100%)',
    padding: '80px 20px 72px',
    textAlign: 'center',
  },
  heroInner: {
    maxWidth: '720px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
  },
  heroBadge: {
    display: 'inline-block',
    background: '#dcfce7',
    color: GREEN,
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '4px 14px',
    borderRadius: '999px',
    border: `1px solid ${GREEN_LIGHT}`,
  },
  h1: {
    fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
    fontWeight: 800,
    lineHeight: 1.2,
    color: '#0f172a',
    margin: 0,
  },
  heroSub: {
    fontSize: '1.1rem',
    color: '#475569',
    margin: 0,
    maxWidth: '560px',
    lineHeight: 1.6,
  },
  ctaBtn: {
    display: 'inline-block',
    background: GREEN,
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    padding: '14px 32px',
    fontSize: '1.05rem',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'background 0.2s, transform 0.15s',
    textDecoration: 'none',
  },
  heroNote: {
    fontSize: '0.83rem',
    color: '#94a3b8',
    margin: 0,
  },

  /* Sections */
  section: {
    padding: '72px 20px',
    background: '#ffffff',
  },
  container: {
    maxWidth: '960px',
    margin: '0 auto',
  },
  h2: {
    fontSize: 'clamp(1.4rem, 3vw, 2rem)',
    fontWeight: 800,
    color: '#0f172a',
    textAlign: 'center',
    margin: '0 0 10px',
  },
  sectionSub: {
    textAlign: 'center',
    color: '#64748b',
    fontSize: '1rem',
    marginBottom: '48px',
  },

  /* Steps */
  stepsRow: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  stepCard: {
    flex: '1 1 240px',
    maxWidth: '280px',
    background: '#f8fafc',
    border: '1.5px solid #e2e8f0',
    borderRadius: '16px',
    padding: '28px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  stepNumber: {
    fontSize: '2rem',
    fontWeight: 800,
    color: GREEN,
    lineHeight: 1,
  },
  stepTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
  },
  stepDesc: {
    fontSize: '0.88rem',
    color: '#64748b',
    margin: 0,
    lineHeight: 1.6,
  },

  /* Templates */
  templatesRow: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  templateCard: {
    flex: '1 1 220px',
    maxWidth: '280px',
    background: '#ffffff',
    border: '1.5px solid #e2e8f0',
    borderRadius: '16px',
    padding: '28px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  templateTag: {
    display: 'inline-block',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.07em',
    padding: '3px 12px',
    borderRadius: '999px',
    alignSelf: 'flex-start',
  },
  templateName: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
  },
  templateDesc: {
    fontSize: '0.86rem',
    color: '#64748b',
    margin: 0,
    lineHeight: 1.55,
  },

  /* Features */
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
  },
  featureCard: {
    background: '#f8fafc',
    border: '1.5px solid #e2e8f0',
    borderRadius: '14px',
    padding: '24px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  featureIcon: {
    fontSize: '1.8rem',
    lineHeight: 1,
  },
  featureTitle: {
    fontSize: '0.97rem',
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
  },
  featureDesc: {
    fontSize: '0.86rem',
    color: '#64748b',
    margin: 0,
    lineHeight: 1.55,
  },

  /* Bottom CTA */
  ctaSection: {
    padding: '72px 20px',
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    textAlign: 'center',
  },
  ctaBox: {
    maxWidth: '600px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  ctaHeading: {
    fontSize: 'clamp(1.4rem, 3vw, 2rem)',
    fontWeight: 800,
    color: '#0f172a',
    margin: 0,
  },
  ctaSub: {
    fontSize: '1rem',
    color: '#475569',
    margin: 0,
  },
};
